import { mapGetters } from 'vuex'
import { preferenceStorageKey } from '@/common/preferencesStorage'
import Papa from 'papaparse'

const radEarth = 6378.16
const oneDegree = (2 * Math.PI * radEarth) / 360
const oneKm = 1 / oneDegree

export default {
  name: 'UtilsMixin',
  data() {
    return {
      tileProvidersReady: false,
      reportRequestInFlight: false,
      statusesMap: {
        DRAFT: ['DRAFT', 'READY', 'CANCELED'],
        WAITING: ['CONFIRMED', 'CANCELED', 'READY'],
        READY: ['CONFIRMED', 'CANCELED', 'READY', 'DRAFT', 'INCIDENCE'],
        BLOCKED: ['READY'],
        CONFIRMED: ['PICKEDUP', 'INCIDENCE', 'ATPICKUPPOINT', 'CANCELED'],
        ATPICKUPPOINT: ['PICKEDUP', 'INCIDENCE', 'CANCELED'],
        PICKEDUP: ['INTRANSIT'],
        INTRANSIT: ['ATDELIVERYPOINT', 'DELIVERED', 'INCIDENCE'],
        INCIDENCE: ['READY', 'CONFIRMED', 'INTRANSIT', 'CANCELED', 'TOBERETURNED'],
        WAITINGCLIENT: ['READY', 'CANCELED', 'TOBERETURNED'],
        TOBERETURNED: ['RETURNED', 'READY'],
        ATDELIVERYPOINT: ['DELIVERED', 'INCIDENCE'],
        DELIVERED: [],
        CANCELED: [],
        RETURNED: []
      },
      statusEvents: {
        CONFIRMED: ['DRAFT', 'READY', 'CONFIRMED', 'ATPICKUPPOINT'],
        INTRANSIT: ['PICKEDUP', 'INTRANSIT', 'INCIDENCE', 'ATDELIVERYPOINT'],
        CANCELED: ['CANCELED'],
        RETURNED: ['RETURNED']
      },
      statusNeedReason: [
        'CANCELED',
        'INCIDENCE',
        'TOBERETURNED'
      ],
      statusReversible: [
        'DELIVERED',
        'CANCELED',
        'TOBERETURNED',
        'RETURNED'
      ],
      distanceCoefficient: 1
    }
  },
  computed: {
    tileProviders() {
      let tileProviders = this.$appConfig.maps.providers
      if (this.ecosystemMaps?.providers) {
        tileProviders = [...new Set(this.$appConfig.maps.providers.concat(this.ecosystemMaps.providers))].filter(Boolean)
      }
      return tileProviders
    },
    hasCompany() {
      return this.profile.id && this.profile.company && this.profile.company.id !== null
    },
    hasMarketingSettings() {
      return this.profile && this.profile.company.marketing_settings.length !== 0 && this.profile.company.marketing_settings.findIndex(x => x.setting.type === 'industry') !== -1
    },
    hasMeliIntegration() {
      return this.profile?.platform_user_authorizations && this.profile.platform_user_authorizations.findIndex(x => x.platform.slug === 'meli') !== -1
    },
    isCompanyConfirmed() {
      return this.profile?.company && this.profile.company.status === 'confirmed'
    },
    courierI18n() {
      return this.$eid === 1 ? this.$gettext('Moover') : this.$gettext('Courier')
    },
    couriersI18n() {
      return this.$eid === 1 ? this.$gettext('Moovers') : this.$gettext('Couriers')
    },
    siteName() {
      return this.$store.getters.universe.siteName
    },
    helpEmail() {
      return this.$store.getters.universe.helpEmail || 'help@moova.io'
    },
    salesEmail() {
      return this.$store.getters.universe.salesEmail || 'help@moova.io'
    },
    unitSystemMultiplier() {
      return this.frontConfig.i18n.unitSystem === 'imperial' ? 1610 : 1000
    },
    appRolesTranslated() {
      return this.appRoles.map(x => {
        return {
          value: x.value,
          text: this.$gettext(x.text)
        }
      })
    },
    ...mapGetters([
      'isAuthenticated',
      'profile',
      'roles',
      'isSuperAdmin',
      'isOperatorSupervisor',
      'isGcmAdmin',
      'isCompanyAdmin',
      'frontConfig',
      'eid',
      'ecosystem',
      'brandName',
      'ecosystemCountries',
      'ecosystemConfig',
      'ecosystemModules',
      'ecosystemPlugins',
      'ecosystemMaps',
      'sidebarPinned',
      'sidebarCollapsed',
      'models',
      'dataTransformers',
      'quickFiltersSetup',
      'plainQuickFiltersSetup',
      'appRoles',
      'vehicleTypes',
      'vehicleTypesSelect',
      'shippingSizes',
      'shippingTypes',
      'shippingTypesShared',
      'shippingSettings',
      'platforms',
      'receiverTypes',
      'shippingStatusReasons',
      'tobereturnedStatusReasons',
      'geoTypes',
      'collection',
      'spinner',
      'vehicleTypeGroupVehicleTypes',
      'commodityVarieties'
    ])
  },
  watch: {
    perPage(val) {
      if (val && this.type) {
        const perPage = this.getLocalStorage(this.preferenceStorageKey('perPage')) || {}
        perPage[this.type] = val
        this.setLocalStorage(this.preferenceStorageKey('perPage'), perPage)
      }
    },
    compact(val) {
      if (this.type) {
        const compact = this.getLocalStorage(this.preferenceStorageKey('compact')) || {}
        compact[this.type] = val
        this.setLocalStorage(this.preferenceStorageKey('compact'), compact)
      }
    }
  },
  methods: {
    setup() {
      this.setupLanguage()
      this.$watch('profile.id', (val) => this.setupLanguage(), { deep: true })

      this.$store.dispatch('setCollapseSidebar', this.$cookies.get('sidebarCollapsed') || 0)
      this.$store.dispatch('setPinSidebar', this.$cookies.get('sidebarPinned') || 0)
      // this.$store.dispatch('checkEcosystem')

      this.$store.dispatch('setVehicleTypes', {
        params: {
          sort: [{
            key: 'max_volume_allowed',
            direction: 'asc'
          }],
          filter_groups: [
            {
              filters: [
                ['vehicle_type_group_id', 'eq', null]
              ]
            }
          ]
        }
      })

      this.$store.dispatch('setShippingSizes', {
        params: {
          sort: [{
            key: 'max_weight',
            direction: 'asc'
          }]
        }
      })

      this.$store.dispatch('setShippingTypes', {
        params: {
          sort: [{
            key: 'name',
            direction: 'asc'
          }]
        }
      })

      this.$store.dispatch('setShippingSettings', {
        params: {
          sort: [{
            key: 'id',
            direction: 'asc'
          }]
        }
      })

      this.$store.dispatch('setPlatforms', {
        params: {
          sort: [{
            key: 'name',
            direction: 'asc'
          }]
        }
      })

      this.$store.dispatch('setReceiverTypes')

      this.$store.dispatch('setShippingStatusReasons')

      this.$store.dispatch('setGeoTypes', {
        params: {
          sort: [{
            key: 'name',
            direction: 'asc'
          }]
        }
      })
    },
    setupLanguage() {
      console.log('*** profile & $language PRE', this.profile.id, this.$language.current)
      this.$language.current = this.profile.id ? this.frontConfig.i18n.lang || 'en_US' : navigator.languages[0].split('-')[0] || 'en_US'
      console.log('*** $language POST', this.$language.current)
      if (this.frontConfig) {
        console.log('*** front_config Moment & TZ', this.frontConfig.i18n.moment, this.frontConfig.i18n.tz)
        this.$tz = this.frontConfig.i18n.tz
        const moment = this.frontConfig.i18n.moment || 'en'
        this.moment.locale(moment)
        this.moment.tz.setDefault(this.$tz)
        window.moment.locale(moment)
        window.moment.tz.setDefault(this.$tz)
      }
      this.$watch('ecosystemConfig', (val, oldVal) => {
        if (!oldVal.i18nOverrides && val.i18nOverrides) {
          console.log('*** Eco $language addTranslations()', val.i18nOverrides)
          this.$language.addTranslations(val.i18nOverrides)
          this.$language.current = 'X'
          setTimeout(() => {
            this.$root.$emit('reloadModels')
            this.$root.$emit('reloadQuickFilters')
            this.$language.current = this.profile.id ? this.frontConfig.i18n.lang || 'en_US' : navigator.languages[0].split('-')[0] || 'en_US'
          }, 500)
        }
      })
    },
    randomInRange(from, to, fixed) {
      fixed = fixed || 10
      return (Math.random() * (to - from) + from).toFixed(fixed) * 1
    },
    jitterize(lat, lng, kms, fixed) {
      return {
        lat: this.randomInRange(
          lat - (kms * oneKm),
          lat + (kms * oneKm),
          fixed
        ),
        lng: this.randomInRange(
          lng - (kms * oneKm),
          lng + (kms * oneKm),
          fixed
        )
      }
    },
    stripHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html')
      return doc.body.textContent || ''
    },
    /**
     * Removes duplicated entries from an array
     * @param  {Array} array The array with duplicated entries
     * @returns {Array}       The array without duplicated entries
     * @public
     */
    arrayUnique(array) {
      const a = array.concat()
      for (let i = 0; i < a.length; ++i) {
        for (let j = i + 1; j < a.length; ++j) {
          if (a[i] === a[j]) {
            a.splice(j--, 1)
          }
        }
      }
      return a
    },
    /**
     * Returns the diff between one given date and now or two dates in a human readable string
     * @param  {String|Date} from            The "from" date (valid string or Date object)
     * @param  {String|Date} [to=Date.now()] The "to" date (valid string or Date object)
     * @returns {String}                 A string with the diff like "32d 15h 23m" (can be negative if "from" is in the future)
     * @public
     */
    elapsedTime(from, to = Date.now()) {
      const diffMs = this.moment.utc(to).diff(this.moment.utc(from), 'milliseconds', true)
      let diffDays = Math.floor(diffMs / 86400000)
      let diffHrs = Math.floor((diffMs % 86400000) / 3600000)
      let diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000)

      // HACK for negative times offset
      diffDays = (diffMs < 0 ? -diffDays - 1 : diffDays)
      diffHrs = (diffMs < 0 ? -diffHrs - 1 : diffHrs)
      diffMins = (diffMs < 0 ? -diffMins : diffMins)

      return (diffMs < 0 ? '-' : '') + (diffDays !== 0 ? diffDays + 'd ' : '') + (diffHrs !== 0 ? diffHrs + 'h ' : '') + (diffMins !== 0 ? diffMins + 'm ' : '0m')
    },
    elapsedTimeRaw(from, to = Date.now()) {
      const diffMs = this.moment.utc(to).diff(this.moment.utc(from), 'milliseconds', true)

      return diffMs
    },
    humanFileSize(bytes, si = false, dp = 1) {
      const thresh = si ? 1000 : 1024

      if (Math.abs(bytes) < thresh) {
        return bytes + ' B'
      }

      const units = si
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
      let u = -1
      const r = 10 ** dp

      do {
        bytes /= thresh
        ++u
      } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)

      return bytes.toFixed(dp) + ' ' + units[u]
    },
    /**
     * Copies to the Clipboard a string. Requires Toast to show copy result.
     * @param  {String} str         The string to copy to Clipboard
     * @param  {String} [type='id'] Optional. The element type you are copying to show the generic or specific result message
     * @param  {Boolean} [singleLine=false] Optional. If false it copies with new lines (adding one before the copied string)
     * @public
     */
    copyToClipboard(str, type = 'id', singleLine = false) {
      str = this.$options.filters.cleanentityprefix(str)
      const handleToast = (success) => {
        let mssg
        let translated
        if (success) {
          switch (type) {
            case 'id':
              mssg = this.$gettext('The ID has been copied to Clipboard')
              break
            case 'email':
              mssg = this.$gettext('The e-mail has been copied to Clipboard')
              break
            case '':
              mssg = this.$gettext('The data has been copied to Clipboard')
              break
            default:
              translated = this.$gettext('The %{type} has been copied to Clipboard')
              mssg = this.$gettextInterpolate(translated, { type })
          }
          this.makeToast(this.$gettext('Notice'), mssg, 'success', false, 2000)
        } else {
          switch (type) {
            case 'id':
              mssg = this.$gettext('The ID could NOT be copied to Clipboard')
              break
            case 'email':
              mssg = this.$gettext('The e-mail could NOT be copied to Clipboard')
              break
            case '':
              mssg = this.$gettext('The data could NOT be copied to Clipboard')
              break
            default:
              translated = this.$gettext('The %{type} could NOT be copied to Clipboard')
              mssg = this.$gettextInterpolate(translated, { type })
          }
          this.makeToast(this.$gettext('Warning'), mssg, 'danger', false, 2000)
        }
      }

      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(str)
          .then(() => handleToast(true))
          .catch(() => handleToast(false))
      } else {
        const el = document.createElement('textarea')
        el.value = str
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select()
        let selection
        let range
        if (!singleLine) {
          selection = document.getSelection()
          range = document.createRange()
        }
        try {
          if (!singleLine) {
            range.selectNode(el)
            selection.removeAllRanges()
            selection.addRange(range)
          }
          const success = document.execCommand('copy')
          if (!singleLine) {
            selection.removeAllRanges()
          }
          handleToast(success)
        } catch (e) {
          handleToast(false)
        }
        document.body.removeChild(el)
      }
    },
    /**
     * Shows a Toast message on screen
     * @param  {String}  title                 The top title of the Toast box
     * @param  {String}  mssg                  The message to show
     * @param  {String}  [variant='info']      The Bootstrap variant for Toast colors
     * @param  {Boolean} [append=false]        Should be appended or prepended?
     * @param  {Number|False} [autoHideDelay=false] Time in ms before auto hide the Toast or false
     * @param  {Boolean} [solid=true]          Opacity style for the Toast
     * @public
     */
    makeToast(title, mssg, variant = 'info', append = false, autoHideDelay = false, solid = true, position = 'top-right') {
      const options = {
        title,
        appendToast: append,
        variant,
        solid,
        toaster: 'b-toaster-' + position
      }
      if (autoHideDelay !== false) {
        options.autoHideDelay = autoHideDelay
      } else {
        options.noAutoHide = true
      }

      this.$bvToast.toast([mssg], options)
    },
    /**
     * Requests an asynchronous report. The backend queues a job and emails the file, so there is
     * nothing to download here — the only output is the notice shown to whoever asked for it.
     *
     * Concentrates the GET → Simplert block that used to be duplicated across every report button.
     * The backend deduplicates in-progress report requests per user (MOOV-5361): an absorbed
     * request answers `409`, and the agreed signal is the status code, never the body text, so the
     * copy shown here is ours and goes through gettext.
     *
     * Note on the global `Vue.http` interceptor in `main.js`: it opens an error Simplert for any
     * status in [402, 500] that carries a `body.message`. `409` lands there, but `openSimplert()`
     * reassigns every field of the single mounted instance, so the notice below replaces it — and
     * since the browser does not paint between microtasks, the error one is never seen. The
     * interceptor runs first by construction: vue-resource only rejects after its whole handler
     * chain has run. It is deliberately NOT silenced with `skipErrorAlert`, so real failures keep
     * the backend detail it already shows (the per-field 422 list, `body.message` on 403/404/500).
     *
     * `reportRequestInFlight` is a `data` prop of this global mixin, so each component gets its own
     * copy — one screen's pending report never disables another's button.
     *
     * @param  {Object} data         Request description
     * @param  {String} data.url     URL to GET
     * @param  {Object} data.params  Query params, already built by the caller
     * @param  {String} data.message Success copy, specific to each report
     * @return {Promise} Resolves once the notice has been shown, so callers can chain
     * @public
     */
    generateReportAsync(data) {
      this.reportRequestInFlight = true

      return this.$http.get(data.url, { params: data.params })
        .then(response => {
          const obj = {
            title: this.$gettext('JOB DISPATCHED'),
            message: data.message,
            type: 'success',
            customCloseBtnText: this.$gettext('Close')
          }
          this.$root.$children[0].$refs.simplert.openSimplert(obj)
        }, error => {
          if (error && error.status === 409) {
            const obj = {
              title: this.$gettext('REPORT ALREADY IN PROGRESS'),
              message: this.$gettext('This report is already being generated. You will receive a single email with the download link as soon as it\'s done'),
              type: 'info',
              customCloseBtnText: this.$gettext('Close')
            }
            this.$root.$children[0].$refs.simplert.openSimplert(obj)
            return
          }
          console.error(error)
          this.makeToast(this.$gettext('ERROR'), this.$gettext('The Report could not be generated. Please, try again'), 'danger', false, 5000)
        })
        .finally(() => {
          this.reportRequestInFlight = false
        })
        // Nothing above rejects, so this only catches a throw inside one of the handlers themselves
        // (a missing simplert ref on some screen). Keeps the returned promise settled either way.
        .catch(err => console.error(err))
    },
    flattenObject(obj) {
      const toReturn = {}

      for (const i in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, i)) continue

        if ((typeof obj[i]) === 'object' && obj[i] !== null) {
          const flatObject = this.flattenObject(obj[i])
          for (const x in flatObject) {
            if (!Object.prototype.hasOwnProperty.call(flatObject, x)) continue

            toReturn[i + '.' + x] = flatObject[x]
          }
        } else {
          toReturn[i] = obj[i]
        }
      }
      return toReturn
    },
    unflattenObject(obj) {
      const result = {}
      for (const i in obj) {
        const keys = i.split('.')
        keys.reduce(function (r, e, j) {
          return r[e] || (r[e] = isNaN(Number(keys[j + 1])) ? (keys.length - 1 === j ? obj[i] : {}) : [])
        }, result)
      }
      return result
    },
    toCsv(array) {
      return Papa.unparse(array)
    },
    /**
     * Auto download the data passed
     * @param  {Object} data Should contain the `data` itself, `type` and `filename` for the download
     * @param  {String}  data                  The data itself
     * @param  {String}  type                  The file type, ie.: text/csv
     * @param  {String}  filename              The name of the file to be downloaded
     * @public
     */
    download(data, type, filename) {
      const blob = new Blob([data], { type })
      if (navigator.msSaveBlob) { // IE 10+
        navigator.msSaveBlob(blob, filename)
      } else {
        const link = document.createElement('a')
        if (link.download !== undefined) { // feature detection
          // Browsers that support HTML5 download attribute
          const url = URL.createObjectURL(blob)
          link.setAttribute('href', url)
          link.setAttribute('download', filename)
          link.style.visibility = 'hidden'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      }
    },
    colorGreenToRed(value) {
      // value from 0 to 1
      const hue = ((1 - value) * 120).toString(10)
      return ['hsl(' + hue + ',80%,50%)'].join('')
    },
    itemColorByAge(daysIdle, module) {
      let color = '#000'
      for (const colorDefinition of this.ecosystemModules[module].icon.colorByMinAge) {
        if (!isNaN(daysIdle) && daysIdle > colorDefinition.days) {
          color = colorDefinition.color
        }
      }
      return color
    },
    stringToColour(str) {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
      }
      let colour = '#'
      for (let n = 0; n < 3; n++) {
        const value = (hash >> (n * 8)) & 0xFF
        colour += ('00' + value.toString(16)).substr(-2)
      }
      return colour
    },
    textContrastColor(str) {
      const c = str.substring(1) // strip #
      const rgb = parseInt(c, 16) // convert rrggbb to decimal
      const r = (rgb >> 16) & 0xff // extract red
      const g = (rgb >> 8) & 0xff // extract green
      const b = (rgb >> 0) & 0xff // extract blue

      const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b // per ITU-R BT.709

      if (luma < 128) {
        return 'color: #fff'
      } else {
        return 'color: #000'
      }
    },
    /**
     * Flexible color manipulation on the fly
     * Explanation https://stackoverflow.com/a/13542669/3783511
     */
    pSBC(p, c0, c1, l) {
      let r
      let g
      let b
      let P
      let f
      let t
      let h
      const i = parseInt
      const m = Math.round
      let a = typeof (c1) === 'string'
      if (typeof (p) !== 'number' || p < -1 || p > 1 || typeof (c0) !== 'string' || (c0[0] !== 'r' && c0[0] !== '#') || (c1 && !a)) return null
      if (!this.pSBCr) {
        this.pSBCr = (d) => {
          let n = d.length
          const x = {}
          if (n > 9) {
            [r, g, b, a] = d = d.split(',')
            n = d.length
            if (n < 3 || n > 4) return null
            x.r = i(r[3] === 'a' ? r.slice(5) : r.slice(4))
            x.g = i(g)
            x.b = i(b)
            x.a = a ? parseFloat(a) : -1
          } else {
            if (n === 8 || n === 6 || n < 4) return null
            if (n < 6) d = '#' + d[1] + d[1] + d[2] + d[2] + d[3] + d[3] + (n > 4 ? d[4] + d[4] : '')
            d = i(d.slice(1), 16)
            if (n === 9 || n === 5) {
              x.r = d >> 24 & 255
              x.g = d >> 16 & 255
              x.b = d >> 8 & 255
              x.a = m((d & 255) / 0.255) / 1000
            } else {
              x.r = d >> 16
              x.g = d >> 8 & 255
              x.b = d & 255
              x.a = -1
            }
          }
          return x
        }
      }
      h = c0.length > 9
      h = a ? c1.length > 9 ? true : c1 === 'c' ? !h : false : h
      f = this.pSBCr(c0)
      P = p < 0
      t = c1 && c1 !== 'c'
        ? this.pSBCr(c1)
        : P
          ? {
              r: 0,
              g: 0,
              b: 0,
              a: -1
            }
          : {
              r: 255,
              g: 255,
              b: 255,
              a: -1
            }
      p = P ? p * -1 : p
      P = 1 - p
      if (!f || !t) return null
      if (l) {
        r = m(P * f.r + p * t.r)
        g = m(P * f.g + p * t.g)
        b = m(P * f.b + p * t.b)
      } else {
        r = m((P * f.r ** 2 + p * t.r ** 2) ** 0.5)
        g = m((P * f.g ** 2 + p * t.g ** 2) ** 0.5)
        b = m((P * f.b ** 2 + p * t.b ** 2) ** 0.5)
      }
      a = f.a
      t = t.a
      f = a >= 0 || t >= 0
      a = f ? a < 0 ? t : t < 0 ? a : a * P + t * p : 0
      if (h) return 'rgb' + (f ? 'a(' : '(') + r + ',' + g + ',' + b + (f ? ',' + m(a * 1000) / 1000 : '') + ')'
      else return '#' + (4294967296 + r * 16777216 + g * 65536 + b * 256 + (f ? m(a * 255) : 0)).toString(16).slice(1, f ? undefined : -2)
    },
    async checkMapProviders() {
      // TODO: clean hardcoded checks using new `order` field
      await this.$http.get(this.tileProviders.find(x => x.name === 'Mapbox').testTile)
        .then(() => {
          this.tileProviders[this.tileProviders.findIndex(x => x.name === 'Mapbox')].visible = true
          this.tileProvidersReady = true
        }, async e => {
          console.log(e)
          this.$root.$children[0].$refs.simplert.justCloseSimplert()
          await this.$http.get(this.tileProviders.find(x => x.name === 'CartoDB').testTile)
            .then(() => {
              this.tileProviders[this.tileProviders.findIndex(x => x.name === 'CartoDB')].visible = true
              this.tileProvidersReady = true
            }, async e => {
              this.$root.$children[0].$refs.simplert.justCloseSimplert()
              console.log(e)
              this.tileProviders[this.tileProviders.findIndex(x => x.name === 'OpenStreetMap')].visible = true
              this.tileProvidersReady = true
            })
        })
    },
    getDistance(point1, point2) {
      const lat1 = point1.lat
      const lon1 = point1.lng

      const lat2 = point2.lat
      const lon2 = point2.lng

      const R = 6371e3 // metres
      const φ1 = this.toRadians(lat1)
      const φ2 = this.toRadians(lat2)
      const Δφ = this.toRadians((lat2 - lat1))
      const Δλ = this.toRadians((lon2 - lon1))

      const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) *
        Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

      const d = R * c
      return parseInt(d)
    },
    toRadians(number) {
      return number * Math.PI / 180
    },
    distance(meters) {
      return this.$options.filters.distance(meters, { unitSystem: this.frontConfig.i18n.unitSystem, locale: this.frontConfig.i18n.moment })
    },
    getSpeed(item1, item2) {
      return (this.getDistance(item1, item2) / this.moment.utc(item2.created_at).diff(this.moment.utc(item1.created_at), 'seconds', true)) * 3.6
    },
    toTitleCase(str) {
      return str.replace(/_/g, ' ').replace(
        /\w\S*/g,
        function (txt) {
          return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        }
      )
    },
    deepDiffMapper() {
      return {
        VALUE_CREATED: 'created',
        VALUE_UPDATED: 'updated',
        VALUE_DELETED: 'deleted',
        VALUE_UNCHANGED: 'unchanged',
        map: function (obj1, obj2) {
          if (this.isFunction(obj1) || this.isFunction(obj2)) {
            console.log('Invalid argument. Function given, object expected.')
          }
          if (this.isValue(obj1) || this.isValue(obj2)) {
            return {
              type: this.compareValues(obj1, obj2),
              data: obj1 === undefined ? obj2 : obj1
            }
          }

          const diff = {}
          for (const key in obj1) {
            if (this.isFunction(obj1[key])) {
              continue
            }

            let value2
            if (obj2[key] !== undefined) {
              value2 = obj2[key]
            }

            diff[key] = this.map(obj1[key], value2)
          }
          for (const key2 in obj2) {
            if (this.isFunction(obj2[key2]) || diff[key2] !== undefined) {
              continue
            }

            diff[key2] = this.map(undefined, obj2[key2])
          }

          return diff
        },
        compareValues: function (value1, value2) {
          if (value1 === value2) {
            return this.VALUE_UNCHANGED
          }
          if (this.isDate(value1) && this.isDate(value2) && value1.getTime() === value2.getTime()) {
            return this.VALUE_UNCHANGED
          }
          if (value1 === undefined) {
            return this.VALUE_CREATED
          }
          if (value2 === undefined) {
            return this.VALUE_DELETED
          }
          return this.VALUE_UPDATED
        },
        fieldsToUpdate: function (newObj, originalObj) {
          let updatedFields = this.map(newObj, originalObj)
          updatedFields = this.nestedCheck(updatedFields, originalObj)
          return updatedFields
        },
        nestedCheck(updatedFields, originalObj) {
          Object.keys(updatedFields).forEach(key => {
            if (this.isValue(originalObj[key])) {
              if (!updatedFields[key] || !updatedFields[key].data || !updatedFields[key].type || updatedFields[key].type !== this.VALUE_UPDATED) {
                delete updatedFields[key]
              } else {
                updatedFields[key] = updatedFields[key].data
              }
            } else {
              this.nestedCheck(updatedFields[key], originalObj[key])
            }
          })
          return updatedFields
        },
        isFunction: function (x) {
          return Object.prototype.toString.call(x) === '[object Function]'
        },
        isArray: function (x) {
          return Object.prototype.toString.call(x) === '[object Array]'
        },
        isDate: function (x) {
          return Object.prototype.toString.call(x) === '[object Date]'
        },
        isObject: function (x) {
          return Object.prototype.toString.call(x) === '[object Object]'
        },
        isValue: function (x) {
          return !this.isObject(x) && !this.isArray(x)
        }
      }
    },
    openMapOn(placeString) {
      window.open('https://www.google.com/maps/place/' + placeString.replace(/#/g, '%23 ').replace(/\+/g, '%2B'))
    },
    getItemDirection(direction) {
      if (this.ecosystemModules.items.directionMap && this.ecosystemModules.items.directionMap[direction]) {
        return this.ecosystemModules.items.directionMap[direction]
      } else if (!isNaN(direction)) {
        return direction
      } else {
        return 0
      }
    },
    getItemDynamic(dynamic) {
      if (this.ecosystemModules.items.dynamicMap && this.ecosystemModules.items.dynamicMap[dynamic]) {
        return this.ecosystemModules.items.dynamicMap[dynamic]
      } else {
        return null
      }
    },
    closeModal(modalRef) {
      this.$refs[modalRef].hide()
    },
    setLocalStorage(key, value, updateRemote = true) {
      if (updateRemote) {
        let meta = this.profile.meta
        if (!meta) {
          meta = {}
        }
        if (!meta.persistentStorage) {
          meta.persistentStorage = {}
        }
        meta.persistentStorage[key] = value
        this.$store.dispatch('updateUser', { meta })
      }

      if (typeof value === 'object') {
        value = JSON.stringify(value)
      }
      localStorage[key] = value
    },
    // Storage key for a user preference: {product}.{ecosystem}.{type} (see common/preferencesStorage)
    preferenceStorageKey(type) {
      return preferenceStorageKey(type, this.$store.getters.eid)
    },
    getLocalStorage(key) {
      try {
        return JSON.parse(localStorage[key])
      } catch (e) {
        return localStorage[key]
      }
    },
    debounceFn(fn, wait) {
      let timer
      return function (...args) {
        if (timer) {
          clearTimeout(timer) // clear any pre-existing timer
        }
        const context = this // get the current context
        timer = setTimeout(() => {
          fn.apply(context, args) // call the function if time expires
        }, wait)
      }
    },
    throttle(fn, wait) {
      let throttled = false
      return function (...args) {
        if (!throttled) {
          fn.apply(this, args)
          throttled = true
          setTimeout(() => {
            throttled = false
          }, wait)
        }
      }
      // },
      // setFilters(filters = this.filters, currentFilters = {}) {
      //   console.log(currentFilters)

    //   filters.columns.forEach(column => {
    //     column.filters.forEach(filter => {
    //       if (filter.name) {
    //         return {...currentFilters, [filter.name]: filter.value}
    //       } else {
    //         this.setFilters(filter, currentFilters)
    //       }
    //     })
    //   })
    }
  }
}
